import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { DirectivesModule } from '@et/directives';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import {
  DEFAULT_DATE_PICKER,
  EtJournalESeal,
  NotarialActType,
} from '@et/typings';
import { convertToUTCDate, transformDate } from '@et/utils';
import {
  DpDatePickerModule,
  IDatePickerDirectiveConfig,
} from 'ng2-date-picker';
import { catchError, EMPTY, finalize } from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';
import { EsealsConverterService } from '../../services/eseals-converter.service';

export interface JournalSealAddEditComponentData {
  seal?: EtJournalESeal;
}

@Component({
  selector: 'et-journal-seal-add-edit',
  imports: [
    ReactiveFormsModule,
    PresentationalAtomsModule,
    MatDialogClose,
    DirectivesModule,
    DpDatePickerModule,
  ],
  templateUrl: './journal-seal-add-edit.component.html',
  styleUrl: './journal-seal-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalSealAddEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef =
    inject<MatDialogRef<JournalSealAddEditComponent>>(MatDialogRef);
  private readonly ejournalService = inject(EjournalService);
  private readonly esealsConverter = inject(EsealsConverterService);
  private readonly dialogData =
    inject<JournalSealAddEditComponentData>(MAT_DIALOG_DATA);

  readonly datePickerConfig: IDatePickerDirectiveConfig = {
    ...DEFAULT_DATE_PICKER,
    format: 'MM/DD/YYYY',
  };

  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly notorialActOptions = Object.values(NotarialActType);

  readonly form = this.fb.nonNullable.group({
    notorialAct: ['', Validators.required],
    documentDescription: ['', Validators.required],
    pageNumber: [null as number | null, Validators.min(1)],
    feeCharged: ['', [Validators.required]],
    notarizationDate: ['', Validators.required],
    notarizationTime: ['', Validators.required],
    notes: [''],
  });

  get isEditMode(): boolean {
    return !!this.dialogData.seal;
  }

  /**
   * Audit-sourced fields (notarization datetime, page) are locked for eSeals.
   */
  get lockAuditFields(): boolean {
    const source = this.dialogData.seal?.source;
    return source === 'eSeal' || source === 'deletedESeal';
  }

  get sourceLabel(): string {
    const source = this.dialogData.seal?.source;
    if (source === 'eSeal') {
      return 'eSeal';
    }
    if (source === 'deletedESeal') {
      return 'Deleted eSeal';
    }
    return 'Seal';
  }

  ngOnInit(): void {
    const seal = this.dialogData.seal;
    if (seal) {
      this.form.patchValue({
        notorialAct: seal.notorialAct ?? '',
        documentDescription: seal.documentDescription ?? '',
        pageNumber: seal.pageNumber || null,
        feeCharged: seal.feeCharged?.toString() ?? '',
        notes: seal.notes ?? '',
        notarizationDate: seal.notarizationDate
          ? transformDate(convertToUTCDate(seal.notarizationDate), 'MM/DD/YYYY')
          : '',
        notarizationTime: seal.notarizationDate
          ? transformDate(seal.notarizationDate, 'HH:mm')
          : '',
      });

      if (this.lockAuditFields) {
        this.form.controls.notarizationDate.clearValidators();
        this.form.controls.notarizationTime.clearValidators();
        this.form.controls.pageNumber.clearValidators();
        this.form.controls.notarizationDate.updateValueAndValidity({
          emitEvent: false,
        });
        this.form.controls.notarizationTime.updateValueAndValidity({
          emitEvent: false,
        });
        this.form.controls.pageNumber.updateValueAndValidity({
          emitEvent: false,
        });
      }
    } else {
      this.form.patchValue({
        feeCharged:
          this.ejournalService.eJournal()?.feeCharged.toString() ?? '',
      });
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const ejournal = this.ejournalService.eJournal();
    if (!ejournal) {
      this.error.set('No eJournal loaded');
      return;
    }

    const raw = this.form.getRawValue();
    const base =
      this.dialogData.seal ??
      this.esealsConverter.createEmptySeal(
        ejournal.packetId,
        ejournal.id,
        Number(ejournal.feeCharged.toString().replace(/[^0-9.]/g, '')),
      );

    const notarizationDate = this.lockAuditFields
      ? base.notarizationDate
      : convertToUTCDate(`${raw.notarizationDate} ${raw.notarizationTime}`);

    const pageNumber = this.lockAuditFields
      ? base.pageNumber
      : raw.pageNumber
      ? Number(raw.pageNumber)
      : 0;

    const nextSeal: EtJournalESeal = {
      ...base,
      documentDescription: raw.documentDescription,
      pageNumber,
      notorialAct: raw.notorialAct,
      notarizationDate,
      feeCharged: Number(raw.feeCharged?.replace(/[^0-9.]/g, '')),
      notes: raw.notes.trim() || undefined,
      signerIds: base.signerIds,
      packetId: base.packetId || ejournal.packetId,
      eJournalPacketId: base.eJournalPacketId || ejournal.id,
      ipAddress: this.ejournalService.ip() || base.ipAddress,
    };

    this.isLoading.set(true);
    this.error.set(null);
    this.dialogRef.disableClose = true;

    this.ejournalService
      .saveSeal(nextSeal)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef.disableClose = false;
        }),
        catchError(() => {
          this.error.set('Failed to save seal');
          return EMPTY;
        }),
      )
      .subscribe(() => this.dialogRef.close());
  }
}
